import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { ProductTotalRow, formatQuantity } from './EndOfDayPrintService';

interface Props {
  rows: ProductTotalRow[];
  loading: boolean;
  error?: string | null;
}

/**
 * Gün sonu "Toplam Ürün" sekmesi: tüm kanallarda satılan her ürünün toplam adedi.
 * Toplamın üstüne gelince kanal kanal dağılımı (Hızlı Satış 50, Restoran 20 ...) görünür.
 */
const ProductTotalsPanel: React.FC<Props> = ({ rows, loading, error }) => {
  const { t } = useTranslation('salesSystem');
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr-TR');
    return q ? rows.filter(r => r.name.toLocaleLowerCase('tr-TR').includes(q)) : rows;
  }, [rows, search]);

  const grandTotal = useMemo(() => visible.reduce((s, r) => s + r.total, 0), [visible]);

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <TextField
          size="small"
          placeholder={t('endOfDayReport.productTotals.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: { xs: '100%', sm: 280 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {t('endOfDayReport.productTotals.hint')}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : visible.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
          {t('endOfDayReport.productTotals.empty')}
        </Typography>
      ) : (
        <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: 56 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('endOfDayReport.productTotals.product')}</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'right', width: 160 }}>{t('endOfDayReport.productTotals.quantity')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((r, i) => (
                <TableRow key={r.name} hover>
                  <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>
                    <Tooltip
                      arrow
                      placement="left"
                      title={(
                        <Box sx={{ minWidth: 160 }}>
                          {r.byChannel.map(c => (
                            <Box key={c.key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                              <span>{c.label}</span>
                              <b>{formatQuantity(c.quantity)}</b>
                            </Box>
                          ))}
                        </Box>
                      )}
                    >
                      <Box component="span" sx={{ fontWeight: 700, cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.secondary' }}>
                        {formatQuantity(r.total)}
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell />
                <TableCell sx={{ fontWeight: 700 }}>
                  {t('endOfDayReport.productTotals.grandTotal')} ({visible.length})
                </TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>{formatQuantity(grandTotal)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default ProductTotalsPanel;
